import { useEffect, useState, useRef } from "react";
import searchPnc from "../../assets/searchPnc.svg";
import folderPnc from "../../assets/folderPnc.svg";
import dropPnc from "../../assets/dropPnc.svg";
import fusoChat from "../../assets/fusoChat.svg";
import "./SearchParameters.css";
import { useTranslation } from "react-i18next";

const SearchParameters = ({
  searchType,
  setSearchType,
  vinInput,
  handleVinInputChange,
  isLoadingVin,
  vinError,
  vinSuggestions = [],
  showVinDropdown = false,
  isLoadingVinSuggestions = false,
  handleVinSuggestionSelect,
  vinDropdownRef,
  chassis1Input = "",
  chassis2Input = "",
  handleChassis1InputChange,
  handleChassis2InputChange,
  isLoadingChassis = false,
  chassisError = null,
  chassis2LengthError = false,
  chassisResults = null,
  modelInput,
  classificationInput,
  handleModelInputChange,
  handleClassificationInputChange,
  isLoadingModelClass,
  modelClassError,
  isSearchValid,
  groups,
  subGroups,
  selectedGroup,
  selectedGroupMainGroup,
  selectedSubGroup,
  selectedSubGroupMainGroup,
  groupDropdownOpen,
  subGroupDropdownOpen,
  setGroupDropdownOpen,
  setSubGroupDropdownOpen,
  handleGroupSelect,
  handleSubGroupSelect,
  isLoadingSubGroups,
  resetSearchState,
  key,
  cycleId,
  isNewCycle = false,
  vinResults = null,
  handleHideVinSuggestions,
  isChangeSelection = false,
  preserveInputs = false,
  preservedData = {},
  hasVinSuggestionsResponse,
  chassis1Suggestions = [],
  chassis2Suggestions = [],
  isLoadingChassis1Suggestions = false,
  isLoadingChassis2Suggestions = false,
  showChassis1Dropdown = false,
  showChassis2Dropdown = false,
  handleChassis1SuggestionSelect,
  handleChassis2SuggestionSelect,
  handleHideChassis1Suggestions,
  handleHideChassis2Suggestions,
  chassis1DropdownRef,
  chassis2DropdownRef,
  hasChassis1SuggestionsResponse = false,
  hasChassis2SuggestionsResponse = false,
  modelSuggestions = [],
  classificationSuggestions = [],
  isLoadingModelSuggestions = false,
  isLoadingClassificationSuggestions = false,
  showModelDropdown = false,
  showClassificationDropdown = false,
  handleModelSuggestionSelect,
  handleClassificationSuggestionSelect,
  handleHideModelSuggestions,
  handleHideClassificationSuggestions,
  modelDropdownRef,
  classificationDropdownRef,
  hasModelSuggestionsResponse = false,
  hasClassificationSuggestionsResponse = false,
}) => {
  const { t } = useTranslation();

  // Use current inputs instead of preserved data to make them editable
  const effectiveVinInput = vinInput;
  const effectiveModelInput = modelInput;
  const effectiveClassificationInput = classificationInput;
  const effectiveChassis1Input = chassis1Input;
  const effectiveChassis2Input = chassis2Input;

  const instanceId = `${cycleId}-search-params`;

  // Modal states for mobile view
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [subGroupModalOpen, setSubGroupModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Create refs for the dropdown elements
  const groupDropdownRef = useRef(null);
  const subGroupDropdownRef = useRef(null);
  
  // NEW: Add ref for the group section to enable auto-scroll
  const groupSectionRef = useRef(null);
  
  // NEW: Add ref for input sections to enable auto-scroll when search type changes
  const inputSectionRef = useRef(null);
  
  // NEW: Track previous state to detect when group section becomes visible
  const [prevGroupSectionVisible, setPrevGroupSectionVisible] = useState(false);
  
  // NEW: Track previous search type to detect when search type changes
  const [prevSearchType, setPrevSearchType] = useState(searchType);

   // Helper function to determine if group/subgroup section should be shown
  const shouldShowGroupSection = () => {
    if (searchType === "vin") {
      // For VIN search, only show if VIN is valid and has results
      return isValidVin() && vinResults;
    } else if (searchType === "chassis") {
      // For Chassis search, only show if Chassis is valid and has results
      return isValidChassis() && chassisResults;
    } else if (searchType === "model") {
      // For model search, show if search is valid
      return isSearchValid;
    }
    return false;
  };

  // Helper function to check if Chassis search is valid
  const isValidChassis = () => {
    const hasChassis1 = chassis1Input.trim().length > 0;
    const hasChassis2 = chassis2Input.trim().length > 0;
    return (
      !chassisError &&
      (hasChassis1 || hasChassis2) &&
      (isSearchValid || chassisResults)
    );
  };

  
  const isValidVinFormat = (vin) => {
    // Remove spaces and convert to uppercase
    const cleanVin = vin.replace(/\s+/g, "").toUpperCase();

    // Check if it's between 8-17 characters and contains valid characters
    const vinRegex = /^[A-HJ-NPR-Z0-9]{8,17}$/;
    return vinRegex.test(cleanVin);
  };

   // Helper function to check if VIN is valid
  const isValidVin = () => {
    const hasValidFormat = vinInput.trim() && isValidVinFormat(vinInput.trim());
    return !vinError && hasValidFormat && (isSearchValid || vinResults);
  };



  const shouldShowChassis1Suggestions = () => {
    // Disabled: No Chassis 1 suggestions
    return false;
  };

  const shouldShowChassis2Suggestions = () => {
    // Disabled: No Chassis 2 suggestions
    return false;
  };

  // Helper function to check if chassis1 value matches any suggestion
  const isChassis1ValueValid = () => {
    // Empty input is considered valid (no error)
    if (!chassis1Input.trim()) return true;

    // No response yet, consider potentially valid
    if (!hasChassis1SuggestionsResponse) return true;

    // No suggestions available but has input = invalid
    if (!chassis1Suggestions || chassis1Suggestions.length === 0) return false;

    // Check if input matches exactly with any suggestion (case-insensitive)
    return chassis1Suggestions.some(suggestion =>
      suggestion.toLowerCase() === chassis1Input.trim().toLowerCase()
    );
  };

  // Helper function to check if chassis2 value matches any suggestion
  const isChassis2ValueValid = () => {
    // Empty input is considered valid (no error)
    if (!chassis2Input.trim()) return true;

    // No response yet, consider potentially valid
    if (!hasChassis2SuggestionsResponse) return true;

    // No suggestions available but has input = invalid
    if (!chassis2Suggestions || chassis2Suggestions.length === 0) return false;

    // Check if input matches exactly with any suggestion (case-insensitive)
    return chassis2Suggestions.some(suggestion =>
      suggestion.toLowerCase() === chassis2Input.trim().toLowerCase()
    );
  };

  const shouldShowChassis1ValidationMessage = () => {
    const hasMinInput = chassis1Input.length >= 1;
    const hasReceivedResponse = hasChassis1SuggestionsResponse;
    const hasNoSuggestions =
      !chassis1Suggestions || chassis1Suggestions.length === 0;
    const isDropdownActive = showChassis1Dropdown;

    // Don't show validation if chassis results are available (successful lookup from get-catalog API)
    const hasChassisResults =
      chassisResults &&
      chassisResults.groups &&
      chassisResults.groups.length > 0;

    // Don't show validation while dropdown is open
    if (isDropdownActive) return false;

    // Don't show validation if we have successful chassis results from get-catalog API
    if (hasChassisResults) return false;

    // Don't show validation while loading chassis (get-catalog API call in progress)
    if (isLoadingChassis) return false;

    // Case 1: No suggestions found at all
    const noSuggestionsFound = (
      !isLoadingChassis1Suggestions &&
      !chassisError &&
      hasMinInput &&
      hasReceivedResponse &&
      hasNoSuggestions
    );

    // Case 2: User typed value doesn't match any suggestions (case-insensitive)
    const typedValueDoesNotMatch = (
      !isLoadingChassis1Suggestions &&
      !chassisError &&
      hasMinInput &&
      hasReceivedResponse &&
      chassis1Suggestions &&
      chassis1Suggestions.length > 0 &&
      !chassis1Suggestions.some(suggestion =>
        suggestion.toLowerCase() === chassis1Input.trim().toLowerCase()
      )
    );

    return noSuggestionsFound || typedValueDoesNotMatch;
  };

  const shouldShowChassis2ValidationMessage = () => {
    const hasMinInput = chassis2Input.length >= 1;
    const hasReceivedResponse = hasChassis2SuggestionsResponse;
    const hasNoSuggestions =
      !chassis2Suggestions || chassis2Suggestions.length === 0;
    const isDropdownActive = showChassis2Dropdown;

    // Don't show validation if chassis results are available (successful lookup from get-catalog API)
    const hasChassisResults =
      chassisResults &&
      chassisResults.groups &&
      chassisResults.groups.length > 0;

    // Don't show validation while dropdown is open
    if (isDropdownActive) return false;

    // Don't show validation if we have successful chassis results from get-catalog API
    if (hasChassisResults) return false;

    // Don't show validation while loading chassis (get-catalog API call in progress)
    if (isLoadingChassis) return false;

    // Case 1: No suggestions found at all
    const noSuggestionsFound = (
      !isLoadingChassis2Suggestions &&
      !chassisError &&
      hasMinInput &&
      hasReceivedResponse &&
      hasNoSuggestions
    );

    // Case 2: User typed value doesn't match any suggestions (case-insensitive)
    const typedValueDoesNotMatch = (
      !isLoadingChassis2Suggestions &&
      !chassisError &&
      hasMinInput &&
      hasReceivedResponse &&
      chassis2Suggestions &&
      chassis2Suggestions.length > 0 &&
      !chassis2Suggestions.some(suggestion =>
        suggestion.toLowerCase() === chassis2Input.trim().toLowerCase()
      )
    );

    return noSuggestionsFound || typedValueDoesNotMatch;
  };

  const shouldShowModelValidationMessage = () => {
    const hasMinInput = effectiveModelInput.length >= 1;
    const hasReceivedResponse = hasModelSuggestionsResponse;
    const hasNoSuggestions =
      !modelSuggestions || modelSuggestions.length === 0;
    const isDropdownActive = showModelDropdown;

    // Don't show validation while dropdown is open
    if (isDropdownActive) return false;

    // Case 1: No suggestions found at all
    const noSuggestionsFound = (
      !isLoadingModelSuggestions &&
      hasMinInput &&
      hasReceivedResponse &&
      hasNoSuggestions
    );

    // Case 2: User typed value doesn't match any suggestions (case-insensitive)
    const typedValueDoesNotMatch = (
      !isLoadingModelSuggestions &&
      hasMinInput &&
      hasReceivedResponse &&
      modelSuggestions &&
      modelSuggestions.length > 0 &&
      !modelSuggestions.some(suggestion =>
        suggestion.toLowerCase() === effectiveModelInput.trim().toLowerCase()
      )
    );

    return noSuggestionsFound || typedValueDoesNotMatch;
  };

  const shouldShowClassificationValidationMessage = () => {
    const hasMinInput = effectiveClassificationInput.length >= 1;
    const hasReceivedResponse = hasClassificationSuggestionsResponse;
    const hasNoSuggestions =
      !classificationSuggestions || classificationSuggestions.length === 0;
    const isDropdownActive = showClassificationDropdown;

    // Don't show validation while dropdown is open
    if (isDropdownActive) return false;

    // Case 1: No suggestions found at all
    const noSuggestionsFound = (
      !isLoadingClassificationSuggestions &&
      hasMinInput &&
      hasReceivedResponse &&
      hasNoSuggestions
    );

    // Case 2: User typed value doesn't match any suggestions (case-insensitive)
    const typedValueDoesNotMatch = (
      !isLoadingClassificationSuggestions &&
      hasMinInput &&
      hasReceivedResponse &&
      classificationSuggestions &&
      classificationSuggestions.length > 0 &&
      !classificationSuggestions.some(suggestion =>
        suggestion.toLowerCase() === effectiveClassificationInput.trim().toLowerCase()
      )
    );

    return noSuggestionsFound || typedValueDoesNotMatch;
  };

  // Check if model value was actually selected from suggestions (not just typed) - case-insensitive
  const isModelValueValid = () => {
    if (!effectiveModelInput.trim()) return false;

    // If we have a response and suggestions exist, check if current value is in the list (case-insensitive)
    if (hasModelSuggestionsResponse && modelSuggestions && modelSuggestions.length > 0) {
      return modelSuggestions.some(suggestion =>
        suggestion.toLowerCase() === effectiveModelInput.trim().toLowerCase()
      );
    }

    // If no suggestions response yet, consider it potentially valid (still checking)
    if (!hasModelSuggestionsResponse) return true;

    return false;
  };

  // Check if classification value was actually selected from suggestions (not just typed) - case-insensitive
  const isClassificationValueValid = () => {
    if (!effectiveClassificationInput.trim()) return false;

    // If we have a response and suggestions exist, check if current value is in the list (case-insensitive)
    if (hasClassificationSuggestionsResponse && classificationSuggestions && classificationSuggestions.length > 0) {
      return classificationSuggestions.some(suggestion =>
        suggestion.toLowerCase() === effectiveClassificationInput.trim().toLowerCase()
      );
    }

    // If no suggestions response yet, consider it potentially valid (still checking)
    if (!hasClassificationSuggestionsResponse) return true;

    return false;
  };

  const handleChassis1SuggestionClick = (suggestion) => {
    if (handleChassis1SuggestionSelect) {
      handleChassis1SuggestionSelect(suggestion);
    }
    if (handleHideChassis1Suggestions) {
      handleHideChassis1Suggestions();
    }
  };

  const handleChassis2SuggestionClick = (suggestion) => {
    if (handleChassis2SuggestionSelect) {
      handleChassis2SuggestionSelect(suggestion);
    }
    if (handleHideChassis2Suggestions) {
      handleHideChassis2Suggestions();
    }
  };

  const getSubGroupsForSelectedGroup = (
    selectedGroupName,
    selectedMainGroup
  ) => {
    // Prioritize the global subGroups array if available (handles language changes)
    if (subGroups && subGroups.length > 0) {
      return subGroups;
    }

    // Fallback to searching in the respective results if subGroups is empty
    if (searchType === "vin" && vinResults && vinResults.groups) {
      const selectedGroupData = vinResults.groups.find(
        (group) =>
          group.group_name === selectedGroupName &&
          group.main_group === selectedMainGroup
      );

      return selectedGroupData ? selectedGroupData.sub_groups || [] : [];
    }

    if (searchType === "chassis" && chassisResults && chassisResults.groups) {
      const selectedGroupData = chassisResults.groups.find(
        (group) =>
          group.group_name === selectedGroupName &&
          group.main_group === selectedMainGroup
      );

      return selectedGroupData ? selectedGroupData.sub_groups || [] : [];
    }

    if (searchType === "model" && groups && groups.length > 0) {
      const selectedGroupData = groups.find(
        (group) =>
          group.group_name === selectedGroupName &&
          group.main_group === selectedMainGroup
      );

      return selectedGroupData ? selectedGroupData.sub_groups || [] : [];
    }

    return [];
  };

  const displayGroups =
    (searchType === "vin" && vinResults && vinResults.groups) ||
      (searchType === "chassis" && chassisResults && chassisResults.groups) ||
      (searchType === "model" && groups && groups.length > 0)
      ? searchType === "vin"
        ? vinResults.groups
        : searchType === "chassis"
          ? chassisResults.groups
          : groups
      : groups || [];

  const displaySubGroups = getSubGroupsForSelectedGroup(
    selectedGroup,
    selectedGroupMainGroup
  );

  // FIXED: Enhanced click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle group dropdown
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(event.target) &&
        groupDropdownOpen
      ) {
        setGroupDropdownOpen(false);
      }

      // Handle subgroup dropdown
      if (
        subGroupDropdownRef.current &&
        !subGroupDropdownRef.current.contains(event.target) &&
        subGroupDropdownOpen
      ) {
        setSubGroupDropdownOpen(false);
      }

      // Handle VIN suggestions dropdown
      if (
        vinDropdownRef?.current &&
        !vinDropdownRef.current.contains(event.target) &&
        showVinDropdown
      ) {
        if (handleHideVinSuggestions) {
          handleHideVinSuggestions();
        }
      }

      // NEW: Handle Chassis1 suggestions dropdown
      if (
        chassis1DropdownRef?.current &&
        !chassis1DropdownRef.current.contains(event.target) &&
        showChassis1Dropdown
      ) {
        if (handleHideChassis1Suggestions) {
          handleHideChassis1Suggestions();
        }
      }

      // NEW: Handle Chassis2 suggestions dropdown
      if (
        chassis2DropdownRef?.current &&
        !chassis2DropdownRef.current.contains(event.target) &&
        showChassis2Dropdown
      ) {
        if (handleHideChassis2Suggestions) {
          handleHideChassis2Suggestions();
        }
      }
    };

    // Add event listener when any dropdown is open
    if (
      groupDropdownOpen ||
      subGroupDropdownOpen ||
      showVinDropdown ||
      showChassis1Dropdown ||
      showChassis2Dropdown
    ) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [
    groupDropdownOpen,
    subGroupDropdownOpen,
    showVinDropdown,
    showChassis1Dropdown,
    showChassis2Dropdown,
    setGroupDropdownOpen,
    setSubGroupDropdownOpen,
    vinDropdownRef,
    chassis1DropdownRef,
    chassis2DropdownRef,
    handleHideVinSuggestions,
    handleHideChassis1Suggestions,
    handleHideChassis2Suggestions,
  ]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    return () => { };
  }, [cycleId]);

  // NEW: Auto-scroll effect when group section becomes visible
  useEffect(() => {
    const currentGroupSectionVisible = shouldShowGroupSection();
    
    // Only scroll if the group section just became visible (transition from false to true)
    if (currentGroupSectionVisible && !prevGroupSectionVisible && groupSectionRef.current) {
      // Small delay to ensure the DOM has updated
      setTimeout(() => {
        groupSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
    
    // Update previous state
    setPrevGroupSectionVisible(currentGroupSectionVisible);
  }, [
    shouldShowGroupSection(), 
    prevGroupSectionVisible,
    vinResults,
    chassisResults,
    isSearchValid
  ]);

  // NEW: Auto-scroll effect when search type changes (for input sections)
  useEffect(() => {
    // Only scroll if the search type changed (not on initial render)
    if (prevSearchType !== searchType && inputSectionRef.current) {
      // Small delay to ensure the DOM has updated with the new input section
      setTimeout(() => {
        inputSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 100);
    }
    
    // Update previous search type
    setPrevSearchType(searchType);
  }, [searchType, prevSearchType]);

  const handleGroupClick = () => {
    if (isMobile) {
      setGroupModalOpen(true);
    } else {
      if (subGroupDropdownOpen) {
        setSubGroupDropdownOpen(false);
      }
      setGroupDropdownOpen(!groupDropdownOpen);
    }
  };

  // Sub-group selection (Optional feature)
  const handleSubGroupClick = () => {
    const hasSubGroups =
      selectedGroup && displaySubGroups && displaySubGroups.length > 0;
    const canOpenSubGroup = hasSubGroups && !isLoadingSubGroups;

    if (canOpenSubGroup) {
      if (isMobile) {
        setSubGroupModalOpen(true);
      } else {
        if (groupDropdownOpen) {
          setGroupDropdownOpen(false);
        }
        setSubGroupDropdownOpen(!subGroupDropdownOpen);
      }
    }
  };

  // Get sub-group status (Optional - shows "Optional" when available)
  const getSubGroupStatus = () => {
    if (!selectedGroup) {
      return { text: t("selectGroupFirst"), disabled: true };
    }

    if (
      (searchType === "model" || searchType === "chassis") &&
      isLoadingSubGroups
    ) {
      return { text: t("loadingSubGroups"), disabled: true };
    }

    // Always show selected value if it exists, regardless of displaySubGroups state
    if (selectedSubGroup && selectedSubGroupMainGroup) {
      return {
        text: `${selectedSubGroup} - ${selectedSubGroupMainGroup}`,
        disabled: false,
      };
    }

    if (displaySubGroups && displaySubGroups.length > 0) {
      return { text: `${t("selectSubGroup")} (${t("optional") || "Optional"})`, disabled: false };
    }

    return { text: t("noSubGroupsAvailable"), disabled: true };
  };

  const subGroupStatus = getSubGroupStatus();

  // FIXED: Enhanced suggestion click handler
  const handleSuggestionClick = (suggestion) => {
    if (handleVinSuggestionSelect) {
      handleVinSuggestionSelect(suggestion);
    }
    // Hide suggestions after selection
    if (handleHideVinSuggestions) {
      handleHideVinSuggestions();
    }
  };

  const shouldShowVinSuggestions = () => {
    // Disabled: No VIN suggestions
    return false;
  };

  // FIXED: Updated VIN validation message function
  const shouldShowVinValidationMessage = () => {
    const hasMinInput = vinInput.length >= 1;
    const hasReceivedResponse = hasVinSuggestionsResponse;
    const hasNoSuggestions = !vinSuggestions || vinSuggestions.length === 0;
    const notLoadingVin = !isLoadingVin;
    const notLoadingSuggestions = !isLoadingVinSuggestions;
    const noVinError = !vinError;
    const noVinResults = !vinResults;

    const shouldShow =
      hasMinInput &&
      hasReceivedResponse &&
      hasNoSuggestions &&
      notLoadingVin &&
      notLoadingSuggestions &&
      noVinError &&
      noVinResults;

    return shouldShow;
  };

  // Function to show chassis validation message
  const shouldShowChassisValidationMessage = () => {
    const hasChassisResults =
      chassisResults &&
      chassisResults.groups &&
      chassisResults.groups.length > 0;

    return (
      !isLoadingChassis &&
      !chassisError &&
      (chassis1Input.length >= 1 || chassis2Input.length >= 1) &&
      !hasChassisResults
    );
  };

  // Function to handle search type change - now allows changing even with preserved inputs
  const handleSearchTypeChange = (newSearchType) => {
    setSearchType(newSearchType);
    resetSearchState();
  };

  return (
    <div key={instanceId} className="fusoChatFirst queryMarkPnc">
      <img src={fusoChat} alt="FUSO" className="w-6 h-6 fusoLogoSmall" />
      <div className="bg-red-50 p-3 pncChatFirst custom-radius">
        <div className="flex gap-3">
          <div className="flex-1">
            {/* Header text for change selections */}
            <p className="gap-[4px] searchHeadPnc">
              <span>
                {isChangeSelection ? t("changeSelections") : t("choosePara")}
              </span>
              <img
                className="targetIconPnc"
                src={searchPnc}
                alt="Search Icon"
              />
            </p>

            {/* Search Type Selector - Now always enabled */}
            <div className="mt-4 space-y-4">
              <div className="flex flex-col md:flex-row gap-[8px]">
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 vinBtnPncNew ${searchType === "vin"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                    }`}
                  style={
                    searchType === "vin"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={() => handleSearchTypeChange("vin")}
                >
                  {t("searchByVin")}
                </button>
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 vinBtnPncNew ${searchType === "chassis"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                    }`}
                  style={
                    searchType === "chassis"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={() => handleSearchTypeChange("chassis")}
                >
                  {t("searchChassis")}
                </button>
                <button
                  className={`px-4 py-2 rounded-lg w-full sm:flex-1 classificationPncNew ${searchType === "model"
                      ? "bg-[#FF0000] text-white"
                      : "border border-red-500 hover:bg-[#FF0000] hover:text-white transition-colors duration-200"
                    }`}
                  style={
                    searchType === "model"
                      ? { backgroundColor: "#FF0000", color: "white" }
                      : {}
                  }
                  onClick={() => handleSearchTypeChange("model")}
                >
                  {t("searchByModel")}
                </button>
              </div>

              {/* VIN Input - Now always editable and wrapped with ref for auto-scroll */}
              {searchType === "vin" && (
                <div ref={inputSectionRef} className="grid gap-4">
                  <p className="flex vinBoxNew">
                    {isChangeSelection ? t("selectedVin") : t("vinSearch")}
                  </p>
                  <div
                    className="relative"
                    ref={vinDropdownRef}
                    style={{ position: "relative", zIndex: 1 }}
                  >
                    {/* VIN Suggestions Dropdown - DISABLED */}
                    {false && shouldShowVinSuggestions() && (
                      <div
                        className="absolute bg-white border border-gray-300 rounded-lg shadow-lg mb-1 vinSuggestionRes"
                        style={{
                          position: "absolute",
                          bottom: "100%",
                          left: 0,
                          zIndex: 9999,
                          minWidth: "15rem",
                          maxHeight: "165px",
                          overflowY: "auto",
                          marginBottom: "4px",
                        }}
                      >
                        {/* Loading suggestions */}
                        {isLoadingVinSuggestions && (
                          <div className="px-4 py-2 text-gray-500 text-sm flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                            {t("loadingSuggestion")}
                          </div>
                        )}

                        {/* Suggestions list */}
                        {!isLoadingVinSuggestions &&
                          vinSuggestions &&
                          vinSuggestions.length > 0 && (
                            <>
                              {vinSuggestions.map((suggestion, index) => (
                                <div
                                  key={`${cycleId}-vin-suggestion-${index}`}
                                  className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                  onClick={() =>
                                    handleSuggestionClick(suggestion)
                                  }
                                >
                                  {suggestion}
                                </div>
                              ))}
                            </>
                          )}

                        {/* No suggestions found */}
                        {!isLoadingVinSuggestions &&
                          vinSuggestions &&
                          vinSuggestions.length === 0 &&
                          vinInput.length >= 3 && (
                            <div className="px-4 py-2 text-gray-500 text-sm">
                              No VIN suggestions found
                            </div>
                          )}
                      </div>
                    )}

                    {/* VIN input - Now always enabled */}
                    <input
                      type="text"
                      placeholder={t("enterVIN")}
                      value={effectiveVinInput}
                      onChange={handleVinInputChange}
                      className="rounded-lg px-4 py-2 w-full sm:w-full md:w-[15rem] lg:w-[19rem] vinInsertNew border border-gray-300 vinInputRes"
                      autoComplete="off"
                      name="vin-input-no-autofill"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                    />

                    {/* VIN Loading indicator */}
                    {isLoadingVin && (
                      <div className="flex items-center text-gray-500 text-sm mt-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        {t("loading")}
                      </div>
                    )}

                    {/* VIN validation messages */}
                    {!isLoadingVin && vinError && (
                      <p className="text-red-500 text-sm mt-2">
                        {t("enterValidVIN")}
                      </p>
                    )}

                    {/* Show validation message when VIN is not valid */}
                    {shouldShowVinValidationMessage() && (
                      <p className="text-red-500 text-sm mt-2">
                        {t("vinNotMatched")}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Chassis Input - NEW SECTION wrapped with ref for auto-scroll */}
              {searchType === "chassis" && (
                <div ref={inputSectionRef} className="grid gap-4">
                  <p className="flex vinBoxNew">
                    {isChangeSelection
                      ? t("selectedChassis")
                      : t("enterChassis")}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Chassis 1 input with suggestions */}
                    <div
                      className="relative"
                      ref={chassis1DropdownRef}
                      style={{ position: "relative", zIndex: 1 }}
                    >
                      {/* Chassis 1 Suggestions Dropdown - DISABLED */}
                      {false && shouldShowChassis1Suggestions() && (
                        <div
                          className="absolute bg-white border border-gray-300 rounded-lg shadow-lg mb-1"
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            left: 0,
                            zIndex: 9999,
                            minWidth: "100%",
                            maxHeight: "165px",
                            overflowY: "auto",
                            marginBottom: "4px",
                          }}
                        >
                          {/* Loading chassis1 suggestions */}
                          {isLoadingChassis1Suggestions && (
                            <div className="px-4 py-2 text-gray-500 text-sm flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                              {t("loadingSuggestion")}
                            </div>
                          )}

                          {/* Chassis1 suggestions list */}
                          {!isLoadingChassis1Suggestions &&
                            chassis1Suggestions &&
                            chassis1Suggestions.length > 0 && (
                              <>
                                {chassis1Suggestions.map(
                                  (suggestion, index) => (
                                    <div
                                      key={`${cycleId}-chassis1-suggestion-${index}`}
                                      className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                      onClick={() =>
                                        handleChassis1SuggestionClick(
                                          suggestion
                                        )
                                      }
                                    >
                                      {suggestion}
                                    </div>
                                  )
                                )}
                              </>
                            )}

                          {/* No chassis1 suggestions found */}
                          {!isLoadingChassis1Suggestions &&
                            chassis1Suggestions &&
                            chassis1Suggestions.length === 0 &&
                            chassis1Input.length >= 3 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {t("ChassisNotFound")}
                              </div>
                            )}
                        </div>
                      )}

                      <input
                        type="text"
                        placeholder={t("chassisNo1")}
                        value={effectiveChassis1Input}
                        onChange={handleChassis1InputChange}
                        className="border border-gray-300 rounded-lg px-4 py-2 vinInsertNew w-full"
                        autoComplete="off"
                        name="chassis1-input-no-autofill"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                      />

                      {/* Chassis1 validation messages */}
                      {shouldShowChassis1ValidationMessage() && (
                        <p className="text-red-500 text-sm mt-2">
                          {t("chassis1NotFound")}
                        </p>
                      )}

                      {/* General chassis validation - moved to Chassis 1 div */}
                      {!isLoadingChassis && chassisError && !chassis2LengthError && (!isChassis1ValueValid() || !isChassis2ValueValid()) && (
                        <p className="text-red-500 text-sm mt-2">
                          {t("validChassis")}
                        </p>
                      )}
                    </div>

                    {/* Chassis 2 input with suggestions */}
                    <div
                      className="relative"
                      ref={chassis2DropdownRef}
                      style={{ position: "relative", zIndex: 1 }}
                    >
                      {/* Chassis 2 Suggestions Dropdown - DISABLED */}
                      {false && shouldShowChassis2Suggestions() && (
                        <div
                          className="absolute bg-white border border-gray-300 rounded-lg shadow-lg mb-1"
                          style={{
                            position: "absolute",
                            bottom: "100%",
                            left: 0,
                            zIndex: 9999,
                            minWidth: "100%",
                            maxHeight: "165px",
                            overflowY: "auto",
                            marginBottom: "4px",
                          }}
                        >
                          {/* Loading chassis2 suggestions */}
                          {isLoadingChassis2Suggestions && (
                            <div className="px-4 py-2 text-gray-500 text-sm flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                              {t("loadingSuggestion")}
                            </div>
                          )}

                          {/* Chassis2 suggestions list */}
                          {!isLoadingChassis2Suggestions &&
                            chassis2Suggestions &&
                            chassis2Suggestions.length > 0 && (
                              <>
                                {chassis2Suggestions.map(
                                  (suggestion, index) => (
                                    <div
                                      key={`${cycleId}-chassis2-suggestion-${index}`}
                                      className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                      onClick={() =>
                                        handleChassis2SuggestionClick(
                                          suggestion
                                        )
                                      }
                                    >
                                      {suggestion}
                                    </div>
                                  )
                                )}
                              </>
                            )}

                          {/* No chassis2 suggestions found */}
                          {!isLoadingChassis2Suggestions &&
                            chassis2Suggestions &&
                            chassis2Suggestions.length === 0 &&
                            chassis2Input.length >= 3 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {t("ChassisNotFound")}
                              </div>
                            )}
                        </div>
                      )}

                      <input
                        type="text"
                        placeholder={t("chassisNo2")}
                        value={effectiveChassis2Input}
                        onChange={handleChassis2InputChange}
                        className="border border-gray-300 rounded-lg px-4 py-2 vinInsertNew w-full"
                        autoComplete="off"
                        name="chassis2-input-no-autofill"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                      />

                      {/* Chassis2 validation messages */}
                      {/* Show length error only when chassis2 is not 7 digits AND no successful API results */}
                      {chassis2LengthError && !isLoadingChassis && !(chassisResults && chassisResults.groups && chassisResults.groups.length > 0) && (
                        <p className="text-red-500 text-sm mt-2">
                          {t("chassis2LengthError")}
                        </p>
                      )}
                      {!chassis2LengthError && shouldShowChassis2ValidationMessage() && (
                        <p className="text-red-500 text-sm mt-2">
                          {t("chassis2NotFound")}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Chassis Loading indicator */}
                  {isLoadingChassis && (
                    <div className="flex items-center text-gray-500 text-sm mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      {t("loading")}
                    </div>
                  )}
                </div>
              )}

              {searchType === "model" && (
                <div ref={inputSectionRef} className="grid">
                  <p className="flex vinBoxNew mb-3">
                    {isChangeSelection
                      ? t("selectedModelClassification")
                      : t("model&ClassificationMsg")}
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Model input with suggestions */}
                    <div className="relative" ref={modelDropdownRef}>
                      {showModelDropdown && (
                        <div className="absolute bottom-full left-0 w-full bg-white border border-gray-300 rounded-lg shadow-lg mb-1 max-h-40 overflow-y-auto z-50">
                          {/* Loading indicator */}
                          {isLoadingModelSuggestions && (
                            <div className="flex items-center justify-center py-3">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                              <span className="text-sm text-gray-500">
                                {t("loading")}
                              </span>
                            </div>
                          )}

                          {/* Model suggestions list */}
                          {!isLoadingModelSuggestions &&
                            modelSuggestions &&
                            modelSuggestions.length > 0 && (
                              <>
                                {modelSuggestions.map(
                                  (suggestion, index) => (
                                    <div
                                      key={`${cycleId}-model-suggestion-${index}`}
                                      className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                      onClick={() =>
                                        handleModelSuggestionSelect(
                                          suggestion
                                        )
                                      }
                                    >
                                      {suggestion}
                                    </div>
                                  )
                                )}
                              </>
                            )}

                          {/* No model suggestions found */}
                          {!isLoadingModelSuggestions &&
                            modelSuggestions &&
                            modelSuggestions.length === 0 &&
                            effectiveModelInput.length >= 1 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {t("ModelNotFound")}
                              </div>
                            )}
                        </div>
                      )}

                      <input
                        type="text"
                        placeholder={t("enterModel")}
                        value={effectiveModelInput}
                        onChange={handleModelInputChange}
                        className="border border-gray-300 rounded-lg px-4 py-2 vinInsertNew w-full"
                        autoComplete="off"
                      />

                      {/* Model validation message */}
                      {shouldShowModelValidationMessage() && (
                        <p className="text-red-500 text-sm mt-2">
                          {t("modelNotFound")}
                        </p>
                      )}
                    </div>

                    {/* Classification input with suggestions */}
                    <div className="relative" ref={classificationDropdownRef}>
                      {showClassificationDropdown && (
                        <div className="absolute bottom-full left-0 w-full bg-white border border-gray-300 rounded-lg shadow-lg mb-1 max-h-40 overflow-y-auto z-50">
                          {/* Loading indicator */}
                          {isLoadingClassificationSuggestions && (
                            <div className="flex items-center justify-center py-3">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                              <span className="text-sm text-gray-500">
                                {t("loading")}
                              </span>
                            </div>
                          )}

                          {/* Classification suggestions list */}
                          {!isLoadingClassificationSuggestions &&
                            classificationSuggestions &&
                            classificationSuggestions.length > 0 && (
                              <>
                                {classificationSuggestions.map(
                                  (suggestion, index) => (
                                    <div
                                      key={`${cycleId}-classification-suggestion-${index}`}
                                      className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                                      onClick={() =>
                                        handleClassificationSuggestionSelect(
                                          suggestion
                                        )
                                      }
                                    >
                                      {suggestion}
                                    </div>
                                  )
                                )}
                              </>
                            )}

                          {/* No classification suggestions found */}
                          {!isLoadingClassificationSuggestions &&
                            classificationSuggestions &&
                            classificationSuggestions.length === 0 &&
                            effectiveClassificationInput.length >= 1 && (
                              <div className="px-4 py-2 text-gray-500 text-sm">
                                {t("ClassificationNotFound")}
                              </div>
                            )}
                        </div>
                      )}

                      <input
                        type="text"
                        placeholder={t("enterClassification")}
                        value={effectiveClassificationInput}
                        onChange={handleClassificationInputChange}
                        className="border border-gray-300 rounded-lg px-4 py-2 vinInsertNew w-full"
                        autoComplete="off"
                      />

                      {/* Classification validation message */}
                      {shouldShowClassificationValidationMessage() && (
                        <p className="text-red-500 text-sm mt-2">
                          {t("classificationNotFound")}
                        </p>
                      )}
                    </div>
                  </div>

                  {isLoadingModelClass && (
                    <div className="flex items-center text-gray-500 text-sm mt-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      {t("loading")}
                    </div>
                  )}
                  {!isLoadingModelClass &&
                    modelClassError &&
                    modelInput.trim() &&
                    classificationInput.trim() &&
                    !shouldShowModelValidationMessage() &&
                    !shouldShowClassificationValidationMessage() &&
                    isModelValueValid() &&
                    isClassificationValueValid() &&
                    (!groups || groups.length === 0) && (
                      <p className="text-red-500 text-sm mt-2">
                        {/* {t("errorFindingData")} */}
                      </p>
                    )}
                </div>
              )}

              {/* Group/Subgroup Selection - NEW: Added ref for auto-scroll */}
              {shouldShowGroupSection() && (
                <div ref={groupSectionRef}>
                  <p className="gap-[4px] mt-[24px] items-center">
                    <span>
                      {isChangeSelection ? t("almostThere") : t("almostThere")}
                    </span>
                    <img
                      className="folderPnc"
                      src={folderPnc}
                      alt="Folder Icon"
                    />
                  </p>
                  {/* Group and optional Sub-Group selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] modelResPnc">
                    {/* Group Selection - Dropdown for desktop, Button for mobile */}
                    <div
                      ref={groupDropdownRef}
                      className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative groupBtnRes"
                    >
                      <div
                        className="w-full cursor-pointer flex justify-between items-center"
                        onClick={handleGroupClick}
                      >
                        <span>
                          {selectedGroup
                            ? `${selectedGroupMainGroup} - ${selectedGroup}`
                            : `${t("selectGroup")} (${t("optional") || "Optional"})`}
                        </span>
                        <img src={dropPnc} alt="Dropdown Icon" />
                      </div>

                      {/* Desktop Dropdown */}
                      {!isMobile && groupDropdownOpen && (
                        <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full max-h-60 overflow-y-auto z-10 shadow-md dropdownStylePnc">
                          {displayGroups &&
                            displayGroups.map((group, index) => (
                              <div
                                key={`${cycleId}-${group.group_name}-${group.main_group}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer"
                                onClick={() => {
                                  handleGroupSelect(
                                    `${group.group_name}|${group.main_group}`,
                                    index
                                  );
                                  setGroupDropdownOpen(false);
                                }}
                              >
                                {group.main_group} - {group.group_name}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>

                    {/* Sub-Group Selection (Optional) - Dropdown for desktop, Button for mobile */}
                    <div
                      ref={subGroupDropdownRef}
                      className="border border-gray-300 rounded-lg px-4 py-2 bg-white vinInsertNew flex dropBtnPnc relative groupBtnRes"
                    >
                      <div
                        className={`w-full cursor-pointer flex justify-between items-center ${subGroupStatus.disabled
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                          }`}
                        onClick={handleSubGroupClick}
                      >
                        <span className="dropSpan">{subGroupStatus.text}</span>
                        <img src={dropPnc} alt="Dropdown Icon" />
                      </div>

                      {/* Desktop Dropdown */}
                      {!isMobile &&
                        subGroupDropdownOpen &&
                        displaySubGroups &&
                        displaySubGroups.length > 0 && (
                          <div className="absolute left-0 bottom-full mb-1 bg-white border border-gray-300 rounded-lg w-full max-h-60 overflow-y-auto z-10 shadow-md dropdownStylePnc">
                            {displaySubGroups.map((subGroup, index) => (
                              <div
                                key={`${cycleId}-${subGroup.group_name}-${subGroup.sub_group}`}
                                className="px-4 py-2 hover:bg-[#FFE0E0] cursor-pointer"
                                onClick={() => {
                                  handleSubGroupSelect(
                                    `${subGroup.sub_group}|${subGroup.group_name}`,
                                    index
                                  );
                                  setSubGroupDropdownOpen(false);
                                }}
                              >
                                {subGroup.sub_group} - {subGroup.group_name}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Group Selection Modal */}
      {isMobile && groupModalOpen && (
        <div className="modal-mi fixed inset-0 bg-black bg-opacity-50 flex justify-center z-50">
          <div className="bg-white rounded-[1.5rem] w-full max-w-sm mx-4 custom-dropModal">
            <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
              <h3 className="text-lg font-medium">{t("selectGroup")} ({t("optional") || "Optional"})</h3>
              <button
                className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                onClick={() => setGroupModalOpen(false)}
              >
                <svg
                  className="svgFeedPnc"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto modal-in">
              {displayGroups &&
                displayGroups.map((group, index) => (
                  <div
                    key={`${cycleId}-${group.group_name}-${group.main_group}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b"
                    onClick={() => {
                      handleGroupSelect(
                        `${group.group_name}|${group.main_group}`,
                        index
                      );
                      setGroupModalOpen(false);
                    }}
                  >
                    {group.main_group} - {group.group_name}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sub-Group Selection Modal (Optional) */}
      {isMobile &&
        subGroupModalOpen &&
        displaySubGroups &&
        displaySubGroups.length > 0 && (
          <div className="fixed modal-mi inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-sm mx-4 custom-dropModal">
              <div className="bg-rose-50 flex justify-between items-center p-4 border-b custom-dropTop">
                <h3 className="text-lg font-medium">{t("selectSubGroup")} ({t("optional") || "Optional"})</h3>
                <button
                  className="text-gray-500 hover:text-gray-700 crossFeedBtnPnc"
                  onClick={() => setSubGroupModalOpen(false)}
                >
                  <svg
                    className="svgFeedPnc"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto modal-in">
                {displaySubGroups.map((subGroup, index) => (
                  <div
                    key={`${cycleId}-${subGroup.group_name}-${subGroup.sub_group}`}
                    className="p-4 hover:bg-[#FFE0E0] cursor-pointer border-b last:border-b-0"
                    onClick={() => {
                      handleSubGroupSelect(
                        `${subGroup.sub_group}|${subGroup.group_name}`,
                        index
                      );
                      setSubGroupModalOpen(false);
                    }}
                  >
                    {subGroup.sub_group} - {subGroup.group_name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default SearchParameters;